import React, { useEffect, useRef, useState } from "react";
import { Box, Stack } from "@mui/material";
import { useForm } from "react-hook-form";
import {
  CustomDrawer,
  CustomModal,
  CustomCalendarInput,
  CustomSingleSelect,
  CustomButton,
  CustomCategorySelectSingle,
  CustomSectionTitle,
  FixedActionBar,
  type CategoryOption,
} from "@ippon/shared-components";
import BillingItemsTable, {
  type BillingTaxCategory,
  hasBillingAmountError,
} from "../BillingItemsTable";
import { Can } from "@/components/auth/Can";

// 収入ステータス (入金管理・収入で共通)。旧 支払予定/未払い/支払済 から差し替え。
// BE 側で旧ステータスは読み取り時に新ステータスへ正規化される (遅延マッピング)。
export const DEFAULT_STATUSES: CategoryOption[] = [
  { value: "未入金", label: "未入金" },
  { value: "入金予定", label: "入金予定" },
  { value: "一部入金", label: "一部入金" },
  { value: "入金済", label: "入金済" },
  { value: "滞納", label: "滞納" },
  { value: "その他", label: "その他" },
];

const TRANSFER_METHODS = [
  { value: "口座振替", label: "口座振替" },
  { value: "顧客振込", label: "顧客振込" },
];

const LABEL_WIDTH = "130px";

/** 当月の aggregation_month 値 ("YYYY-MM") */
function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}


export interface PaymentItemEntry {
  key: string;
  /** BudgetCategory.category_id (内訳の真値)。ローカル新規追加分は保存時まで空。 */
  category_id: string;
  name: string;
  amount: string;
  /** 税区分。非課税 / 課税(10%)。 */
  tax_category: BillingTaxCategory;
  /**
   * ローカルで新規追加された科目 (まだ BudgetCategory 未作成)。
   * 親の入金保存時に createBudgetCategory で永続化し category_id を確定する。
   */
  isNew?: boolean;
}

// 新規登録時に「常に最低1行」表示する用の空明細 (支出側と同方針。税区分は課税が既定)。
const createEmptyPaymentItem = (): PaymentItemEntry => ({
  key: `new_${Date.now()}`,
  category_id: "",
  name: "",
  amount: "",
  tax_category: "taxable",
});

// 明細の dirty 判定用シリアライズ (key は除外。中身のみ比較)。
const serializePaymentItems = (rows: PaymentItemEntry[]): string =>
  JSON.stringify(
    rows.map((r) => [
      r.category_id || "",
      (r.name || "").trim(),
      String(r.amount ?? ""),
      r.tax_category || "non_taxable",
    ]),
  );

export interface PaymentDrawerFormValues {
  status: string;
  items: PaymentItemEntry[];
  /** 収入タイトル (任意。showTitleField=true の収入軸のみ入力)。 */
  title: string;
  aggregation_month?: string;
  scheduled_date: string;
  transfer_datetime: string;
  transfer_method: "口座振替" | "顧客振込" | "";
  remarks: string;
}

/**
 * 入金登録時に選べる科目選択肢。
 * BudgetCategory (収入) を起点に与える。
 */
export interface SectionPaymentItemOption {
  category_id: string;
  name: string;
  defaultAmount: number;
  /** カテゴリ表示 (貸室/付帯/その他)。明細テーブルのカテゴリ列に出す。 */
  categoryLabel?: string;
}

// 明細科目セレクトを収益カテゴリ (貸室→付帯→その他→カスタム) 順に安定ソートする。
// 同カテゴリ内は元の並び (=新規追加は末尾) を保つので、新規科目はそのグループの最下部に出る。
const REVENUE_GROUP_LABEL_ORDER = ["貸室", "付帯", "その他"];
const sortByRevenueGroupLabel = (
  items: SectionPaymentItemOption[],
): SectionPaymentItemOption[] => {
  const rank = (label?: string): number => {
    const i = REVENUE_GROUP_LABEL_ORDER.indexOf(label ?? "");
    return i === -1 ? REVENUE_GROUP_LABEL_ORDER.length : i;
  };
  return [...items].sort(
    (a, b) => rank(a.categoryLabel) - rank(b.categoryLabel),
  );
};

interface PaymentDrawerProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: PaymentDrawerFormValues) => Promise<void>;
  defaultValues?: Partial<PaymentDrawerFormValues>;
  title?: string;
  statusOptions?: CategoryOption[];
  onAddNewStatus?: (value: string) => void;
  sectionPaymentItems?: SectionPaymentItemOption[];
  /** 科目の新規登録 (即時作成)。明細セレクトの「新規登録」モーダルから呼ばれる。 */
  onAddPaymentCategory?: (
    name: string,
    revenueGroup: string,
  ) => Promise<string | undefined>;
  /** 収益カテゴリの選択肢 (新規登録モーダル用。3つ + 既存カスタム)。 */
  revenueGroupOptions?: { value: string; label: string }[];
  /**
   * 「請求項目」セレクトで科目の新規追加を許可するか。
   * true の時、セレクトに「追加」UI を出す。追加は **ローカルのみ** (isNew 内訳行)。
   * 実際の BudgetCategory 作成は親の入金保存時 (onSubmit 受け側) でまとめて行う。
   */
  allowAddPaymentItem?: boolean;
  /**
   * フォーム先頭 (請求年月の上) に差し込む追加フィールド。
   * 収入サブタブのカテゴリ / 区画セレクト等を親から差し込むのに使う。
   */
  leadingFields?: React.ReactNode;
  /**
   * 明示的に編集モード指定。未指定時は `defaultValues` の有無で判定。
   * 新規(false) → registerModal、編集(true) → CustomDrawer。
   */
  isEdit?: boolean;
}

const PaymentDrawer: React.FC<PaymentDrawerProps> = ({
  open,
  onClose,
  onSubmit,
  defaultValues,
  title = "請求登録",
  statusOptions = DEFAULT_STATUSES,
  onAddNewStatus,
  sectionPaymentItems = [],
  onAddPaymentCategory,
  revenueGroupOptions,
  allowAddPaymentItem = false,
  leadingFields,
  isEdit,
}) => {
  const isEditMode = isEdit ?? Boolean(defaultValues);
  const {
    control,
    reset,
    watch,
    setValue,
    getValues,
    formState: { isSubmitting, isDirty },
  } = useForm<PaymentDrawerFormValues>({
    defaultValues: {
      status: "",
      items: [],
      title: "",
      aggregation_month: currentMonth(),
      scheduled_date: "",
      transfer_datetime: "",
      transfer_method: "",
      remarks: "",
      ...defaultValues,
    },
  });

  const statusValue = watch("status");

  const [itemEntries, setItemEntries] = useState<PaymentItemEntry[]>(
    defaultValues?.items || [],
  );
  // 閉じ確認(未保存ガード)用: 開いた時点の明細スナップショット。RHF 外の明細編集を
  // dirty として拾うために使う (confirmOnDirtyClose は RHF isDirty だけでは不足)。
  const initialItemsSerializedRef = useRef<string>(serializePaymentItems([]));

  useEffect(() => {
    if (open) {
      const resetValues = {
        status: "",
        items: [] as PaymentItemEntry[],
        title: "",
        aggregation_month: currentMonth(),
        scheduled_date: "",
        transfer_datetime: "",
        transfer_method: "" as const,
        remarks: "",
        ...defaultValues,
      };
      reset(resetValues);
      // 新規登録(非編集)で明細が無い時は最低1行をデフォルト表示する (支出と挙動を揃える)。
      const nextItems =
        resetValues.items.length > 0
          ? resetValues.items
          : isEditMode
            ? []
            : [createEmptyPaymentItem()];
      setItemEntries(nextItems);
      initialItemsSerializedRef.current = serializePaymentItems(nextItems);
      setItemError(false);
      setStatusError(false);
    }
  }, [open, defaultValues, reset, isEditMode]);

  // RHF フィールド + 明細のどちらかが初期から変わっていれば未保存あり。
  const itemsDirty =
    serializePaymentItems(itemEntries) !== initialItemsSerializedRef.current;
  const formDirty = isDirty || itemsDirty;

  const [itemError, setItemError] = useState(false);
  // 科目未選択の行 key。該当行の科目セレクト直下に「科目は必須です」を出す。
  const [categoryRequiredErrorItemIds, setCategoryRequiredErrorItemIds] =
    useState<Set<string>>(() => new Set());
  const [statusError, setStatusError] = useState(false);
  // エラー時のスクロール先 (モーダル内)。
  const statusFieldRef = useRef<HTMLDivElement>(null);
  const billingSectionRef = useRef<HTMLDivElement>(null);

  const handleFormSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    const currentStatus = getValues("status");
    const hasStatusError = !currentStatus || !currentStatus.trim();
    // 科目未選択の空行を除いた有効明細が 1 件以上必要。
    const hasItemError =
      itemEntries.filter(
        (it) => it.category_id || (it.isNew && it.name.trim()),
      ).length === 0;
    // 科目未選択の行を検出し、該当行の科目セレクトにインライン必須エラーを出す。
    const categoryMissingIds = new Set(
      itemEntries
        .filter((it) => !(it.category_id || (it.isNew && it.name.trim())))
        .map((it) => it.key),
    );

    // 金額の桁数オーバー (12桁以上) は BillingItemsTable 側が赤字表示するので、
    // ここでは送信を止めるだけ。
    const hasAmountError = hasBillingAmountError(itemEntries);

    setStatusError(hasStatusError);
    setItemError(hasItemError);
    setCategoryRequiredErrorItemIds(categoryMissingIds);

    if (
      hasStatusError ||
      hasItemError ||
      hasAmountError ||
      categoryMissingIds.size > 0
    ) {
      // エラー時はモーダル内で該当箇所へスクロール (上にある status を優先)。
      // DOM 更新(エラー表示)後に走らせるため requestAnimationFrame。
      requestAnimationFrame(() => {
        const target = hasStatusError
          ? statusFieldRef.current
          : billingSectionRef.current;
        target?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
      return;
    }

    const data = getValues();
    await onSubmit({ ...data, items: itemEntries });
    onClose();
  };

  // 請求年月 (aggregation_month) の選択肢 — 過去 30 月 + 未来 30 月 (計 61 件・約 5 年分)
  // 現在月を中央に置く配置にし、ドロップダウン展開時に今月付近が視界に入りやすくする。
  const billingMonthOptions = (() => {
    const now = new Date();
    const opts: { value: string; label: string }[] = [];
    for (let offset = 30; offset >= -30; offset--) {
      const d = new Date(now.getFullYear(), now.getMonth() - offset, 1);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      opts.push({ value: `${y}-${m}`, label: `${y}年${m}月` });
    }
    return opts;
  })();

  // 入力フィールド本体（Drawer / registerModal で共有）
  const formFields = (
    <Stack spacing={2}>
      {/* セクションタイトルは全幅。入力フィールドだけ 600 幅に制限する。 */}
      <Box>
        <CustomSectionTitle title="基本情報" size="small" />
      </Box>
      {/* 基本情報フィールドは 600 幅。明細テーブル+合計だけ下で 800 に広げる。 */}
      <Box sx={{ maxWidth: "600px", width: "100%" }}>
        <Stack spacing={2}>
          <Box sx={{ "& > *": { margin: "0 !important" } }}>
            <CustomSingleSelect
              label="請求年月"
              name="aggregation_month"
              control={control as any}
              options={billingMonthOptions}
              placeholder="選択してください"
              labelWidth={LABEL_WIDTH}
              inputWidth="100%"
              hasSpacing={false}
              isRequired
              onChangeValue={(val) => {
                setValue("aggregation_month", String(val || ""), {
                  shouldDirty: true,
                });
              }}
            />
          </Box>
          {leadingFields}

          <Box
            ref={statusFieldRef}
            sx={{ "& > *": { margin: "0 !important" } }}
          >
            <CustomCategorySelectSingle
              label="ステータス"
              name="status"
              options={statusOptions}
              selectedValue={statusValue}
              onValueChange={(val) => {
                setValue("status", val, {
                  shouldDirty: true,
                  shouldTouch: true,
                });
                if (val && val.trim()) setStatusError(false);
              }}
              onAddNewOption={onAddNewStatus}
              isRequired
              labelWidth={LABEL_WIDTH}
              inputWidth="100%"
              errors={
                statusError
                  ? { status: { message: "ステータスは必須です" } }
                  : {}
              }
            />
          </Box>

          <CustomCalendarInput
            name="scheduled_date"
            control={control as any}
            label="入金期日"
          />

          <CustomCalendarInput
            name="transfer_datetime"
            control={control as any}
            label="入金日"
          />

          <Box sx={{ "& > *": { margin: "0 !important" } }}>
            <CustomSingleSelect
              name="transfer_method"
              control={control as any}
              label="入金方法"
              options={TRANSFER_METHODS}
              placeholder="選択してください"
              labelWidth={LABEL_WIDTH}
              inputWidth="100%"
              hasSpacing={false}
            />
          </Box>

        </Stack>
      </Box>

      <Box ref={billingSectionRef}>
        <CustomSectionTitle title="請求内容" size="small" />
      </Box>
      {/* 請求明細テーブル + 合計。基本情報(600)より広く全幅(800)で表示。 */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: { xs: "100%", md: 860 },
          maxWidth: "100%",
        }}
      >
        <BillingItemsTable
          items={itemEntries.map((e) => ({
            item_id: e.key,
            category_id: e.category_id,
            name: e.name,
            amount: e.amount,
            tax_category: e.tax_category,
            isNew: e.isNew,
          }))}
          onChange={(rows) => {
            setItemEntries(
              rows.map((r) => ({
                key: r.item_id,
                category_id: r.category_id,
                name: r.name,
                amount: r.amount,
                tax_category: r.tax_category,
                isNew: r.isNew,
              })),
            );
            setItemError(false);
            // 既にエラー表示中なら入力に追従して再計算 (解消した行は赤を消す)。
            setCategoryRequiredErrorItemIds((prev) =>
              prev.size > 0
                ? new Set(
                    rows
                      .filter(
                        (r) =>
                          !(
                            r.category_id ||
                            (r.isNew && (r.name || "").trim())
                          ),
                      )
                      .map((r) => r.item_id),
                  )
                : prev,
            );
          }}
          categoryOptions={sortByRevenueGroupLabel(sectionPaymentItems).map(
            (s) => ({
              value: s.category_id,
              label: s.name,
              categoryLabel: s.categoryLabel,
            }),
          )}
          allowAddCategory={allowAddPaymentItem}
          onAddCategory={onAddPaymentCategory}
          revenueGroupOptions={revenueGroupOptions}
          addLabel="明細を追加"
          errorMessage={itemError ? "明細は1件以上必要です" : undefined}
          categoryRequiredErrorItemIds={categoryRequiredErrorItemIds}
        />
      </Box>
    </Stack>
  );

  if (!isEditMode) {
    // 新規登録: registerModal で表示
    return (
      <CustomModal
        openModal={open}
        handleCloseModal={onClose}
        title={title}
        modalType="registerModal"
        addTitleBorder
        confirmOnDirtyClose={formDirty}
        sx={{ maxWidth: "800px !important" }}
      >
        <Box
          component="form"
          onSubmit={handleFormSubmit}
          onKeyDown={(e) => {
            // Enter での暗黙送信を抑止 (登録/更新はボタン押下時のみ)。textarea の改行は許可。
            if (
              e.key === "Enter" &&
              (e.target as HTMLElement).tagName !== "TEXTAREA"
            ) {
              e.preventDefault();
            }
          }}
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          <Box className="registerModalBody">
            {formFields}
          </Box>
          <Box className="registerModalFooter">
            <CustomButton
              label="戻る"
              buttonCategory="cancelModal"
              onClick={onClose}
              sx={{ minWidth: "140px !important", width: "140px" }}
            />
            <Can action="payment:edit">
              <CustomButton
                label="登録"
                buttonCategory="submit"
                type="submit"
                isLoading={isSubmitting}
                sx={{ minWidth: "140px !important", width: "140px" }}
              />
            </Can>
          </Box>
        </Box>
      </CustomModal>
    );
  }

  // 編集: CustomDrawer。更新ボタンは下部固定フッター (他のページ詳細と同じ)。
  return (
    <CustomDrawer
      open={open}
      onClose={onClose}
      title={title}
      width={800}
      confirmOnDirtyClose={formDirty}
      sx={{
        width: { xs: "100%", md: 800 },
        // 工事詳細の FixedActionBar に合わせ、フッターの上区切り線は無し。
        "& .drawerFooter": { borderTop: "none" },
      }}
      showFooter
      footerContent={
        // 工事詳細などと同じ共有 FixedActionBar (背景 #F7FBFD・中央・更新ボタン)。
        // CustomDrawer の footer スロットに置くため position は static にし、
        // drawerFooter の padding を相殺して背景を端まで全幅にする。
        <FixedActionBar
          embedded
          submitLabel="更新"
          isLoading={isSubmitting}
          onSubmit={() => handleFormSubmit()}
          sx={{
            position: "static",
            mt: "-16px",
            mb: "-20px",
            mx: "-24px",
          }}
        />
      }
    >
      <Box
        component="form"
        onSubmit={handleFormSubmit}
        onKeyDown={(e) => {
          // Enter での暗黙送信を抑止 (更新はボタン押下時のみ)。textarea の改行は許可。
          if (
            e.key === "Enter" &&
            (e.target as HTMLElement).tagName !== "TEXTAREA"
          ) {
            e.preventDefault();
          }
        }}
      >
        {formFields}
      </Box>
    </CustomDrawer>
  );
};

export default PaymentDrawer;
